import { supabase } from '@/lib/supabase'

export type PlanAssetPolicyMode = 'all' | 'limited'

export type PlanAssetPolicy = {
  plan_id: string
  mode: PlanAssetPolicyMode
  allowed_symbols: string[]
}

function normalizeSymbols(symbols: Array<string | null | undefined>): string[] {
  const set = new Set<string>()
  for (const raw of symbols || []) {
    const s = String(raw || '').trim().toUpperCase()
    if (!s) continue
    set.add(s)
  }
  return Array.from(set.values()).sort()
}

export async function getPlanAssetPolicy(planId: string): Promise<PlanAssetPolicy | null> {
  const key = (planId || '').trim()
  if (!key) return null
  const { data, error } = await supabase
    .from('plan_asset_policies')
    .select('plan_id,mode,allowed_symbols')
    .eq('plan_id', key)
    .maybeSingle()
  if (error) throw error
  if (!data) return null
  const row: any = data
  const mode = row.mode === 'limited' ? 'limited' : 'all'
  const syms = Array.isArray(row.allowed_symbols) ? row.allowed_symbols : []
  return {
    plan_id: String(row.plan_id || key),
    mode,
    allowed_symbols: normalizeSymbols(syms),
  }
}

export async function getPlanAssetPolicyPublic(planId: string): Promise<PlanAssetPolicy | null> {
  const key = (planId || '').trim()
  if (!key) return null
  const { data, error } = await supabase.rpc('get_plan_asset_policy_public', { p_plan_id: key })
  if (error) throw error
  const row: any = Array.isArray(data) ? data[0] : data
  if (!row) return null
  const mode = row.mode === 'limited' ? 'limited' : 'all'
  const syms = Array.isArray(row.allowed_symbols) ? row.allowed_symbols : []
  return {
    plan_id: String(row.plan_id || key),
    mode,
    allowed_symbols: normalizeSymbols(syms),
  }
}

export async function upsertPlanAssetPolicy(input: { planId: string; mode: PlanAssetPolicyMode; symbols: string[] }): Promise<void> {
  const planId = (input.planId || '').trim()
  if (!planId) throw new Error('Plano inv\u00E1lido')
  const normalized = normalizeSymbols(input.symbols || [])
  const mode: PlanAssetPolicyMode = normalized.length > 0 ? input.mode : 'all'
  const payload = {
    plan_id: planId,
    mode,
    allowed_symbols: normalized,
  }
  const { error } = await supabase
    .from('plan_asset_policies')
    .upsert(payload, { onConflict: 'plan_id' })
  if (error) throw error
}

export async function getAllowedSymbolsForCurrentUser(): Promise<string[] | null> {
  const { data, error } = await supabase.rpc('user_allowed_symbols_for_opportunities')
  if (error) throw error
  if (!data) return null
  const arr = Array.isArray(data) ? (data as any[]) : []
  const normalized = normalizeSymbols(arr as string[])
  return normalized.length > 0 ? normalized : null
}
